import { prepareXandrKeywords } from '../prepareKeywords';
import getUserId from '../getUserId';
import type { AppnexusSettings, PlacementType, GetAdnSettings } from '../types';
import { getScreenOrientation } from '../../../utils/screenOrientation';
import { getJWPlayerProvider } from '../../../utils/player/player';
import { getGlimrPermissionOnce } from '../glimr';

import { addCuePoint, getAdCuePoint } from './inFeedAdExperiment';
import { getGlobalKeywords } from './keywords';
import { getAdPermission } from '../getAdPermission';

const getAdnSettings: GetAdnSettings = async (
  context,
  _opts,
  _stream,
  defaults,
  player
) => {
  const { adnPlugin } = context;
  if (!adnPlugin) {
    return null;
  }

  const {
    options,
    siteConfig,
    asset,
    prerollFormat,
    prerollSubtype,
    skipAd,
    muted,
  } = adnPlugin;

  // no ads on brandstudio assets
  if (!siteConfig || !asset || asset.provider === 'brandstudio') {
    return null;
  }

  getGlimrPermissionOnce({ player, config: siteConfig.glimr });

  const adCuePoint = getAdCuePoint(asset, options?.experiments ?? []);
  const enableInFeedAdExperiment = adCuePoint
    ? addCuePoint(player, adCuePoint)
    : false;

  const categoryId = asset.category.id;

  const enableDynamicAspectRatio =
    options.experiments?.find((e) => e.id === 'ads-aspect-ratio')?.variant ===
      'true' && getScreenOrientation() === 'portrait';

  function getAdSchedule(placementType: PlacementType, format = placementType) {
    let placement: string | undefined;

    placement = placement || adnPlugin!.getPlacement(placementType);

    if (placement) {
      return prepareXandrKeywords({
        'id': placement,
        'kw_no-sno-adformat': format,
        'kw_no-sno-news-tv_category': categoryId
          ? String(categoryId)
          : undefined,
      });
    }

    return null;
  }

  const cmpConsent = await getAdPermission(context.consentManager);

  const globalKeywords = await getGlobalKeywords(
    {
      ...defaults,
      ...siteConfig.keywords,
      newsSection: options.ads?.sectionKeywords,
    },
    asset,
    adnPlugin.options.site,
    cmpConsent,
    enableDynamicAspectRatio,
    player,
    siteConfig
  );

  const settings: AppnexusSettings = {
    member: siteConfig.member,
    adPlacements: null,

    options: {
      autoplayMuted: muted && ['bumper', 'sponsorroll'].includes(prerollFormat),
      vpaidcontrols: true,
    },

    adSlots: {
      schedule: {
        preroll: getAdSchedule(prerollSubtype, prerollFormat),
        midroll: getAdSchedule('midroll', 'preroll'),
        postroll: getAdSchedule('postroll', 'postroll'),
      },
      params: globalKeywords,
    },
  };

  if (
    !enableDynamicAspectRatio &&
    adnPlugin.hasPlacement('pausead') &&
    getJWPlayerProvider(player) !== 'chromecast'
  ) {
    settings.adPlacements = {
      schedule: {
        pause: {
          sizes: [[1280, 720]],
          invCode: adnPlugin.getPlacement('pausead')!,
          keywords: prepareXandrKeywords({
            'kw_no-sno-adformat': 'netboard_pausead',
            'kw_no-sno-news-tv_category': categoryId
              ? String(categoryId)
              : undefined,
          }),
          maxDisplays: 1,
        },
      },

      params: {
        keywords: globalKeywords,
      },
    };
  }

  if (enableInFeedAdExperiment) {
    Object.assign(settings.adSlots.schedule, {
      preroll: null,
      midroll: settings.adSlots.schedule.preroll,
    });
  } else if (skipAd) {
    settings.adSlots.schedule.preroll = null;
  }

  const userId = getUserId();

  if (userId) {
    settings.adSlots.params.external_uid = userId;

    if (settings.adPlacements) {
      settings.adPlacements.params.keywords = {
        ...settings.adPlacements.params.keywords,
        external_uid: userId,
      };
    }
  }

  return settings;
};

export default getAdnSettings;
